import React from "react";
import {
  AlertCircleIcon,
  InformationCircleIcon,
} from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { Alert, Lead, Mark, Message } from "./styles/banner";

export type BannerTone = "info" | "caution";

/**
 * A standing note above a form.
 *
 * The mark was `alert.svg` under a hue-rotate filter that landed it on a
 * fluorescent green — a green exclamation disc inside an accent-tinted box,
 * which in this product says "done" while the box says "information", and
 * neither is what the note is for. It is the product's own glyph now, from
 * the icon set every other page draws from.
 *
 * Two tones. `info` states how something works; `caution` says a thing
 * cannot be done at the moment, and takes the warning glyph and a bold lead
 * line so it is read before the form under it rather than after a field
 * complains. Colour lives in the glyph, not in a wash across the pane.
 */
const Banner = ({
  children,
  tone = "info",
  lead,
  ...restProps
}: {
  children?: React.ReactNode;
  tone?: BannerTone;
  /** The first line, in ink and bold — what the note is about. */
  lead?: React.ReactNode;
  [key: string]: any;
}) => {
  return (
    <Alert {...restProps}>
      <Mark $tone={tone}>
        <HugeIcon
          icon={tone === "caution" ? AlertCircleIcon : InformationCircleIcon}
          size={16}
        />
      </Mark>
      <Message>
        {lead ? <Lead>{lead}</Lead> : null}
        {children}
      </Message>
    </Alert>
  );
};

export default Banner;
